import { type ImpersonatedUser, type LoggedInUser } from '@airreading/common/data';
import { useContext, useMemo } from 'react';

import { AuthContext } from '@/auth/context.js';
import { assertNotNullish } from '@/lib/common-utils.js';

/**
 * A logged-in user with various client-side calculated properties.
 */
export interface ClientLoggedInUser extends LoggedInUser {
  /**
   * TODO: deprecate this calculated property.
   */
  fullName: string | null;
}

export interface ClientImpersonatedUser extends ImpersonatedUser {
  fullName: string | null;
}

export type ClientActingUser = ClientLoggedInUser | ClientImpersonatedUser;

export interface SessionState {
  loggedInUser: ClientLoggedInUser;

  /**
   * The user who the app is currently using to view and interact with data.
   * If there is an impersonated user, this will be the impersonated user.
   * If there is no impersonated user, this will be the logged-in user.
   */
  actingUser: ClientActingUser;
  isImpersonating: boolean;
  logout: AuthContext['logout'];
}

export function useSession(): SessionState {
  const { loggedInUser: _loggedInUser, logout } = useContext(AuthContext);

  assertNotNullish(
    _loggedInUser,
    'Logged-in user not found. Please use AuthContext if you are handling potential unauthenticated state. useSession hook should only be used in authenticated routes.',
  );

  const loggedInUser = useMemo<ClientLoggedInUser>(
    () => ({
      ..._loggedInUser,
      fullName: _loggedInUser.fullNameOrEmail,
    }),
    [_loggedInUser],
  );

  const impersonatedUser = useMemo<ClientImpersonatedUser | null>(() => {
    if (!loggedInUser.impersonatingAs) return null;
    return {
      ...loggedInUser.impersonatingAs,
      fullName: loggedInUser.impersonatingAs.fullNameOrEmail,
    };
  }, [loggedInUser]);

  return {
    loggedInUser,
    logout,
    actingUser: impersonatedUser || loggedInUser,
    isImpersonating: !!impersonatedUser,
  };
}
